import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { memo, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Meta from '../../../_tools/meta';
import { ViewOptionsView } from '../../../_types/Car';
import CarListView from '../../../components/car-list-view';
import CarListViewEndless from '../../../components/car-list-view/endlessLoad';
import CustomBreadcrumbs from '../../../components/custom-breadcrumbs';
import EmptyContent from '../../../components/empty-content/component';
import FormProvider from '../../../components/hook-form/form-provider/component';
import { useAllFacetsContext } from '../../../context/all-facets';
import { useRootContainerContext } from '../../../context/root-container';
import { useSettingsContext } from '../../../context/settings';
import useCarSearch from '../../../hooks/use-car-search';
import useContact from '../../../hooks/use-contact';
import { useResponsive } from '../../../hooks/use-responsive';
import useLocales from '../../../hooks/useLocales';
import getSearchDesc from '../../../utils/texte/getSearchDesc';
import getSearchHeading from '../../../utils/texte/getSearchHeading';
import getStyleQuery from '../../../utils/tools/getStyleQuery';
import FahrzeugSucheFilterChips from '../filter/chips-large-screen';
import { FahrzeugSucheFilterSideBar } from '../filter/sidebar';
import FahrzeugSucheFilterSort from '../filter/sort';
import { FahrzeugSucheFilterTopArea } from '../filter/toparea';
import { fahrzeugsucheModuleName } from '../types';
import { type FahrzeugsucheListProps } from './types';

function FahrzeugsucheList(props: FahrzeugsucheListProps) {
    const { allFaarenVehicles, filterElements } = props;
    const allFacets = useAllFacetsContext();
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const { translate } = useLocales();
    const params = useParams<Record<'carDesign' | 'carId' | 'hersteller' | 'modell' | 'typ' | 'standort', string>>();
    const [query] = useSearchParams();
    const navigate = useNavigate();
    const { canReset, formKey, handleSubmit, listMode, loading, methods, notFound, reset, results, setListMode, setValue, values } = useCarSearch({
        allFaarenVehicles,
        disableAutoLoad: false,
        isContact: false,
        module: fahrzeugsucheModuleName,
        navigate,
        params: settings?.general?.useOldRouter === true ? undefined : params,
        query,
    });
    const { contactButtons, contactDialog } = useContact({
        allFaarenVehicles,
        btns: { message: true, priceAlert: true, phone: true, testDrive: true, tradeIn: true },
        car: undefined,
        loading,
        module: fahrzeugsucheModuleName,
    });
    const md = useResponsive(rootContainer, 'down', 'lg');
    const sm = useResponsive(rootContainer, 'down', 'md');
    const useTopFilter = md || settings?.fahrzeugsuche?.topFilter || false;

    const description = useMemo(() => getSearchDesc(translate, allFacets, values), [allFacets, translate, values]);
    const heading = useMemo(() => getSearchHeading(translate, allFacets, values, settings), [translate, allFacets, values, settings]);

    useEffect(
        function () {
            if (sm) {
                setListMode(ViewOptionsView.Grid);
            }
        },
        [setListMode, sm]
    );

    const chips = (
        <FahrzeugSucheFilterChips
            canReset={!settings?.general.mockup && canReset}
            facets={results?.facets}
            filterElements={filterElements}
            filters={values}
            loading={loading}
            module={fahrzeugsucheModuleName}
            onResetFilters={reset}
            onFilters={setValue}
            results={results?.numFound}
            sx={{ [getStyleQuery()]: { width: 1 } }}
        />
    );

    const carList = (settings?.[fahrzeugsucheModuleName]?.endlessScrolling === true
        ? <CarListViewEndless
            cars={results?.cars}
            filter={values}
            list={false}
            loading={loading}
            mini={false}
            module={fahrzeugsucheModuleName}
            navigate={undefined}
            numFound={results?.numFound}
            onFilters={setValue}
        />
        : <CarListView
            cars={results?.cars}
            filter={values}
            list={listMode === ViewOptionsView.List}
            loading={loading}
            mini={false}
            module={fahrzeugsucheModuleName}
            navigate={navigate}
            numFound={results?.numFound}
            onFilters={setValue}
        />
    );

    const header = (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
            {settings?.fahrzeugsuche?.hideHeading ? (
                <Box sx={{ [getStyleQuery()]: { mt: settings?.fahrzeugsuche?.hideHeading && !settings?.fahrzeugsuche?.useLocationMap ? 1 : undefined, width: 1 } }}>{chips}</Box>
            ) : (
                <CustomBreadcrumbs heading={heading?.withPage} />
            )}
            {!sm && <FahrzeugSucheFilterSort filters={values} listMode={listMode} module={fahrzeugsucheModuleName} onFilters={setValue} setListMode={setListMode} />}
        </Stack>
    );

    return (
        <Stack gap={2} data-testid="fahrzeugsuche">
            <Meta module={fahrzeugsucheModuleName} title={heading} desc={description} filter={values} isFiltered={canReset} noLimit type="list" />
            {useTopFilter ? (
                <>
                    {!md && header}
                    <FormProvider formKey={formKey} methods={methods} onSubmit={handleSubmit}>
                        <FahrzeugSucheFilterTopArea
                            filters={values}
                            filterElements={filterElements}
                            loading={loading}
                            module={fahrzeugsucheModuleName}
                            results={results}
                            canReset={!settings?.general.mockup && canReset}
                            onFilters={setValue}
                            onResetFilters={reset}
                        />
                    </FormProvider>
                    {!settings?.fahrzeugsuche?.hideHeading && chips}
                    {notFound ? <EmptyContent filled title={translate('common:errorHandler.sIntErrors.2x404.title')} sx={{ [getStyleQuery()]: { maxHeight: 200, py: 3 } }} /> : carList}
                </>
            ) : (
                <>
                    <Stack gap={!settings?.fahrzeugsuche?.hideHeading && !settings?.fahrzeugsuche?.useLocationMap ? 1 : undefined}>
                        {!md && header}
                        {!settings?.fahrzeugsuche?.hideHeading && chips}
                    </Stack>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, lg: 3 }}>
                            <FormProvider formKey={`${fahrzeugsucheModuleName}_FormProvider`} methods={methods} onSubmit={handleSubmit}>
                                <FahrzeugSucheFilterSideBar filterElements={filterElements} loading={loading} results={results} />
                            </FormProvider>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 9 }}>
                            {notFound ? <EmptyContent filled title={translate('common:errorHandler.sIntErrors.2x404.title')} sx={{ [getStyleQuery()]: { maxHeight: 200, py: 3 } }} /> : carList}
                        </Grid>
                    </Grid>
                </>
            )}
            {contactButtons}
            {contactDialog}
        </Stack>
    );
}

export default memo(FahrzeugsucheList);
